import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ClientTestimonials = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials?.length, isAutoPlaying]);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
    setIsAutoPlaying(false);
  };

  const goToTestimonial = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  if (!testimonials || testimonials?.length === 0) return null;

  const currentTestimonial = testimonials?.[currentIndex];

  return (
    <div className="relative bg-card rounded-2xl overflow-hidden shadow-3d border border-border/50">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="w-full h-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      <div className="relative p-8 lg:p-12">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center space-x-3 mb-4"
          >
            <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
              <Icon name="MessageSquare" size={24} className="text-accent" />
            </div>
            <h3 className="text-2xl font-bold text-foreground">Client Success Stories</h3>
          </motion.div>
          <p className="text-muted-foreground">
            Hear from our clients about their experience working with Shutter Lifters
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="relative min-h-[400px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, rotateY: -90, scale: 0.8 }}
              animate={{ opacity: 1, rotateY: 0, scale: 1 }}
              exit={{ opacity: 0, rotateY: 90, scale: 0.8 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="w-full max-w-4xl mx-auto text-center transform-3d"
            >
              {/* Quote Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                  <Icon name="Quote" size={32} className="text-accent" />
                </div>
              </div>

              {/* Testimonial Content */}
              <blockquote className="text-xl lg:text-2xl font-medium text-foreground mb-8 leading-relaxed">
                "{currentTestimonial?.content}"
              </blockquote>

              {/* Project Results */}
              {currentTestimonial?.results && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {currentTestimonial?.results?.map((result, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="bg-muted/30 rounded-lg p-4"
                    >
                      <div className="text-2xl font-bold text-accent mb-1">
                        {result?.value}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {result?.metric}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Client Info */}
              <div className="flex items-center justify-center space-x-4">
                <div className="relative">
                  <Image
                    src={currentTestimonial?.avatar}
                    alt={currentTestimonial?.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-accent/30"
                  />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-card flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-card" />
                  </div>
                </div>
                <div className="text-left">
                  <h4 className="font-semibold text-foreground">
                    {currentTestimonial?.name}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {currentTestimonial?.position}
                  </p>
                  <p className="text-sm text-accent">
                    {currentTestimonial?.company}
                  </p>
                </div>
              </div>

              {/* Project Badge */}
              {currentTestimonial?.projectType && (
                <div className="mt-4">
                  <span className="px-3 py-1 bg-accent/10 text-accent text-sm rounded-full border border-accent/30">
                    {currentTestimonial?.projectType}
                  </span>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Controls */}
        <div className="flex items-center justify-center space-x-4 mt-8">
          <Button
            variant="ghost"
            size="sm"
            iconName="ChevronLeft"
            onClick={prevTestimonial}
            className="text-muted-foreground hover:text-foreground"
          />
          
          {/* Dots Indicator */}
          <div className="flex items-center space-x-2">
            {testimonials?.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-accent w-8' :'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
              />
            ))}
          </div>

          <Button
            variant="ghost"
            size="sm"
            iconName="ChevronRight"
            onClick={nextTestimonial}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        {/* Auto-play Toggle */}
        <div className="flex justify-center mt-4">
          <Button
            variant="ghost"
            size="sm"
            iconName={isAutoPlaying ? "Pause" : "Play"}
            iconPosition="left"
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            {isAutoPlaying ? 'Pause' : 'Play'} Auto-rotation
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientTestimonials;